// src/Store/pharmacySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from "axios";
import { setLoading } from "./LoadingSlice";
import BaseUrl from '../../BaseUrl';

const url = BaseUrl.pharmacyurl

// Define the initial state
const initialState = {
  inventory: [],
  medicines: [], // New state for medicines
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed' for inventory
  medicineStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed' for medicines
  error: null,
};

// Async thunk for fetching inventory
export const fetchInventory = createAsyncThunk(
  'pharmacy/fetchInventory',
  async (_, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${url}PharmacyPurchases`);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      throw new Error('Failed to fetch inventory.');
    }
  }
);

// Async thunk for adding a new purchase
export const addPurchase = createAsyncThunk(
  'pharmacy/addPurchase',
  async (purchaseData, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(`${url}PharmacyPurchases`, purchaseData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      const errorMessage = error.response?.data?.message || 'Failed to add purchase.';
      throw new Error(errorMessage);
    }
  }
);

// Async thunk for fetching the list of medicines
export const fetchMedicines = createAsyncThunk(
  'pharmacy/fetchMedicines',
  async (_, { dispatch }) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.get(`${url}MedicineMasters`);
      dispatch(setLoading(false));
      return response.data;
    } catch (error) {
      dispatch(setLoading(false));
      throw new Error('Failed to fetch medicine list.');
    }
  }
);

const pharmacySlice = createSlice({
  name: 'pharmacy',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Inventory
      .addCase(fetchInventory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchInventory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.inventory = action.payload;
      })
      .addCase(fetchInventory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Add Purchase
      .addCase(addPurchase.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addPurchase.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.inventory.push(action.payload);
      })
      .addCase(addPurchase.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Fetch Medicines
      .addCase(fetchMedicines.pending, (state) => {
        state.medicineStatus = 'loading';
      })
      .addCase(fetchMedicines.fulfilled, (state, action) => {
        state.medicineStatus = 'succeeded';
        state.medicines = action.payload;
      })
      .addCase(fetchMedicines.rejected, (state, action) => {
        state.medicineStatus = 'failed';
        state.error = action.error.message;
      });
  },
});

export default pharmacySlice.reducer;